import { IconNames } from '@dixa/lib-shared-habla';
import { Channel, Media, media } from 'lib-shared-types-channels';
import groupBy from 'lodash/groupBy';

import type { Presence } from 'apis/presence';
import {
  getChannelFeatureFlagsStatus,
  getMediaFeatureFlagStatus,
} from 'utils/featureFlagging/channelFeatureFlags';

import { managedChannelMap, mediaToChannels } from './channels';

export type MetaWorkingMedia = {
  readonly name: Media;
  readonly channels: readonly Channel[];
  readonly enabled: boolean;
  readonly working: boolean;
  readonly onChannels: readonly Channel[];
  readonly offChannels: readonly Channel[];
};

const mediaSortRankings: Record<Media, number> = {
  speak: 1,
  email: 2,
  interactivechat: 3,
  messaging: 4,
};

function getWorkingStatus(
  channels: Channel[],
  workingChannels: Presence['working_channels'],
): boolean {
  return (
    channels
      // Since voicemail is toggleable separately we don't include it in the meta working status
      .filter((channel) => channel !== 'voicemail')
      .some(
        (channel) =>
          workingChannels.find(
            (workingChannel) => workingChannel.channel === channel,
          )?.working,
      )
  );
}

function groupChannelsByStatus(
  channels: Channel[],
  workingChannels: Presence['working_channels'],
) {
  const { true: onChannels = [], false: offChannels = [] } = groupBy(
    channels,
    (channel) =>
      workingChannels.find(
        (workingChannel) =>
          workingChannel.channel === (managedChannelMap[channel] ?? channel),
      )?.working,
  );
  return { onChannels, offChannels };
}

/**
 * Creates an object of metadata for each media type. Metadata includes:
 *
 * - Enabled: whether any channels under that media are enabled (feature flag)
 * - Working: whether the user is working or away
 * - Channels: the channels under that media (filtered by feature flag)
 * - Name: the name of the media
 *
 * @param workingChannels
 * @returns
 */
export function getMetaWorkingMedia(
  workingChannels: Presence['working_channels'],
): MetaWorkingMedia[] {
  const mediaFeatureFlagStatus = getMediaFeatureFlagStatus();
  const channelFeatureFlagsStatus = getChannelFeatureFlagsStatus();

  return media
    .reduce((metaWorkingMedia, mediaName) => {
      const channels = mediaToChannels[mediaName].filter((channel) =>
        channelFeatureFlagsStatus.get(channel),
      ) as Channel[];

      return [
        ...metaWorkingMedia,
        {
          ...groupChannelsByStatus(channels, workingChannels),
          name: mediaName,
          channels,
          enabled: mediaFeatureFlagStatus.get(mediaName),
          working: getWorkingStatus(channels, workingChannels),
        } as MetaWorkingMedia,
      ];
    }, [] as MetaWorkingMedia[])
    .filter((media) => media.enabled)
    .sort((a, b) => {
      return mediaSortRankings[a.name] - mediaSortRankings[b.name];
    });
}

/** Represents various media specific attributes and configurations. */
export const mediaAttributes: Readonly<
  Record<
    Media,
    {
      /** The icon of the channel. */
      readonly icon: IconNames;
      /** Legacy icon */
      readonly legacyIcon: IconNames;
      /** The pretty name of the channel. */
      readonly name: string;
    }
  >
> = {
  speak: { name: 'Voice', icon: 'media-voice', legacyIcon: 'phone' },
  email: { name: 'Email', icon: 'media-email', legacyIcon: 'mail' },
  interactivechat: {
    name: 'Chat',
    icon: 'media-chat',
    legacyIcon: 'chat',
  },
  messaging: {
    name: 'Messaging',
    icon: 'media-messaging',
    legacyIcon: 'messaging-settings',
  },
};
